// src/pages/MeusDocumentos/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../contexts/ClientContext';
import './MeusDocumentos.css';

// ── Constantes ────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || '';

const TIPOS_FILTRO = [
  { value: 'TODOS',    label: 'Todos' },
  { value: 'ANEXADO',  label: 'Anexado' },
  { value: 'GERADO',   label: 'Gerado' },
  { value: 'EXTRAIDO', label: 'Extraído (OCR)' },
  { value: 'AUDIO',    label: 'Áudio Transcrito' },
];

// ── Tipos aceites para OCR (imagens / PDF) ────────────────────────────────────
const MIME_OCR = [
  'application/pdf',
  'image/jpeg', 'image/jpg', 'image/png',
  'image/tiff', 'image/gif', 'image/bmp', 'image/webp',
];
const EXT_OCR = ['.pdf', '.jpg', '.jpeg', '.png', '.tiff', '.tif', '.gif', '.bmp', '.webp'];

// ── Tipos aceites para Áudio (Speech-to-Text) ────────────────────────────────
const MIME_AUDIO = [
  'audio/mpeg', 'audio/mp3',
  'audio/wav', 'audio/wave', 'audio/x-wav',
  'audio/flac', 'audio/x-flac',
  'audio/ogg',
  'audio/webm', 'video/webm',
];
const EXT_AUDIO = ['.mp3', '.wav', '.flac', '.ogg', '.webm'];

// Todos os tipos aceites (para o input file)
const EXT_TODOS = [...EXT_OCR, ...EXT_AUDIO];

const STATUS_LABEL = {
  pending:    { label: 'Pendente',    cls: 'status-pending'    },
  processing: { label: 'A processar', cls: 'status-processing' },
  done:       { label: 'Concluído',   cls: 'status-done'       },
  error:      { label: 'Erro',        cls: 'status-error'      },
};

const POLLING_INTERVAL = 6000; // 6 segundos

// ── Ícones SVG inline ─────────────────────────────────────────────────────────
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const IconCopy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const IconAI = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);
const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const IconUpload = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
const IconSearch = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IconX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconRefresh = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);
const IconMic = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatarData(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function tipoLabel(doc) {
  if (doc.origem === 'speech_to_text')  return 'ÁUDIO';
  if (doc.origem === 'document_ai_ocr') return 'EXTRAÍDO';
  if (doc.tipo_documento === 'GERADO')  return 'GERADO';
  return doc.tipo_documento || 'ANEXADO';
}

function tipoCls(doc) {
  if (doc.origem === 'speech_to_text')  return 'badge-audio';
  if (doc.origem === 'document_ai_ocr') return 'badge-extraido';
  if (doc.tipo_documento === 'GERADO')  return 'badge-gerado';
  return 'badge-anexado';
}

function formatarCodCliente(val) {
  return val.replace(/\D/g, '').slice(0, 5);
}

/** Detecta se o ficheiro é áudio pelo MIME ou extensão */
function detectarTipoFicheiro(file) {
  if (MIME_AUDIO.includes(file.type)) return 'audio';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (EXT_AUDIO.includes(ext)) return 'audio';
  if (MIME_OCR.includes(file.type) || EXT_OCR.includes(ext)) return 'ocr';
  return null;
}

// ── Componente principal ──────────────────────────────────────────────────────
const MeusDocumentos = () => {
  const { clienteAtual } = useClient();
  const navigate = useNavigate();

  // Estado filtros
  const [filtroTipo, setFiltroTipo]     = useState('TODOS');
  const [filtroCodCli, setFiltroCodCli] = useState('');

  // Estado lista
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [erro, setErro]             = useState('');

  // Estado upload (OCR + Áudio partilham o mesmo painel)
  const [mostrarUpload, setMostrarUpload]   = useState(false);
  const [uploadFile, setUploadFile]         = useState(null);
  const [tipoFicheiro, setTipoFicheiro]     = useState(null); // 'ocr' | 'audio'
  const [uploadCodCli, setUploadCodCli]     = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus]     = useState('idle');
  const [uploadMsg, setUploadMsg]           = useState('');
  const pollingRef  = useRef(null);
  const fileInputRef = useRef(null);

  // Estado dialog viewer
  const [dialog, setDialog] = useState({ open: false, titulo: '', texto: '' });

  // ── Preencher cod_cli do contexto ──────────────────────────────────────────
  useEffect(() => {
    if (clienteAtual?.codCliente && !filtroCodCli)
      setFiltroCodCli(String(clienteAtual.codCliente).padStart(5, '0'));
  }, [clienteAtual?.codCliente, filtroCodCli]);

  useEffect(() => {
    if (clienteAtual?.codCliente && !uploadCodCli)
      setUploadCodCli(String(clienteAtual.codCliente).padStart(5, '0'));
  }, [clienteAtual?.codCliente, uploadCodCli]);

  // ── Carregar documentos ────────────────────────────────────────────────────
  const carregarDocumentos = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const params = {};
      if (filtroTipo !== 'TODOS') params.tipo_documento = filtroTipo;
      if (filtroCodCli.trim())    params.cod_cli = filtroCodCli.trim();

      const res = await axios.get(`${API_BASE}/api/documentos/meus-documentos`, { params });
      if (res.data.success) {
        setDocumentos(res.data.data);
      } else {
        setErro(res.data.message || 'Erro ao carregar documentos');
      }
    } catch (e) {
      setErro('Erro ao carregar documentos: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroCodCli]);

  useEffect(() => { carregarDocumentos(); }, [carregarDocumentos]);

  // ── Polling genérico (OCR e Áudio partilham a mesma lógica) ───────────────
  const iniciarPolling = useCallback((jobId, endpoint) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/api/${endpoint}/status/${jobId}`);
        const { status } = res.data.data;
        if (status === 'done') {
          clearInterval(pollingRef.current);
          setUploadStatus('done');
          setUploadMsg('✅ Transcrição concluída! Documento disponível na listagem.');
          carregarDocumentos();
        } else if (status === 'error') {
          clearInterval(pollingRef.current);
          setUploadStatus('error');
          setUploadMsg('❌ Erro no processamento. Tente novamente.');
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    }, POLLING_INTERVAL);
  }, [carregarDocumentos]);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── Selecção de ficheiro ───────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tipo = detectarTipoFicheiro(file);
    if (!tipo) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      setErro(`Tipo não suportado: ${ext}. Use PDF, imagens (OCR) ou MP3/WAV/FLAC/OGG/WebM (áudio).`);
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setErro('Ficheiro demasiado grande. Máximo: 500MB');
      return;
    }
    setErro('');
    setUploadFile(file);
    setTipoFicheiro(tipo);
    setUploadStatus('idle');
    setUploadMsg('');
    setUploadProgress(0);
  };

  // ── Processo unificado (OCR ou Áudio) ─────────────────────────────────────
  const iniciarTranscricao = async () => {
    if (!uploadFile) { setErro('Seleciona um ficheiro primeiro'); return; }

    const endpoint = tipoFicheiro === 'audio' ? 'audio' : 'ocr';

    setErro('');
    setUploadStatus('uploading');
    setUploadProgress(0);
    setUploadMsg('A gerar URL de upload...');

    try {
      // Determinar MIME type final
      const allMime = [...MIME_OCR, ...MIME_AUDIO];
      const mimeType = allMime.includes(uploadFile.type)
        ? uploadFile.type
        : tipoFicheiro === 'audio' ? 'audio/mpeg' : 'application/pdf';

      // PASSO 1: Pedir Signed URL
      const step1 = await axios.post(`/api/${endpoint}/signed-url`, {
        fileName:       uploadFile.name,
        mimeType,
        fileSize:       uploadFile.size,
        tipo_documento: tipoFicheiro === 'audio' ? 'audio_transcricao' : 'digitalizado_ocr',
        cod_cli:        uploadCodCli ? parseInt(uploadCodCli) : null,
      });

      const { jobId, signedUrl, gcsKey, fileNameSanitizado } = step1.data.data;
      setUploadMsg('A fazer upload para o servidor...');

      // PASSO 2: Upload direto para GCS (idêntico para OCR e áudio)
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', mimeType);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable)
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`GCS upload failed: ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Erro de rede no upload'));
        xhr.send(uploadFile);
      });

      setUploadProgress(100);
      setUploadStatus('processing');

      if (tipoFicheiro === 'audio') {
        setUploadMsg('Upload concluído. A iniciar transcrição de áudio (pt-PT)...');
      } else {
        setUploadMsg('Upload concluído. A iniciar transcrição OCR...');
      }

      // PASSO 3: Disparar processamento no backend
      await axios.post(`/api/${endpoint}/process`, {
        jobId, gcsKey, fileNameSanitizado, mimeType,
      });

      const msgEspera = tipoFicheiro === 'audio'
        ? '⏳ A transcrever áudio... Ficheiros longos podem demorar alguns minutos. Podes fechar este painel — o documento aparecerá na listagem quando pronto. Usa 🔄 para actualizar.'
        : '⏳ A transcrever documento... Podes fechar este painel — o documento aparecerá na listagem quando pronto. Usa o botão 🔄 para actualizar.';

      setUploadMsg(msgEspera);
      iniciarPolling(jobId, endpoint);

    } catch (e) {
      console.error('Transcricao error:', e);
      setUploadStatus('error');
      setUploadMsg('❌ Erro: ' + (e.response?.data?.message || e.message));
    }
  };

  // ── Ver texto ──────────────────────────────────────────────────────────────
  const verTexto = async (doc) => {
    try {
      const res = await axios.get(`/api/documentos/${doc.id}/texto`);
      if (res.data.success) {
        setDialog({ open: true, titulo: doc.file_name, texto: res.data.data.texto_integral || '(sem conteúdo)' });
      }
    } catch (e) {
      setErro('Erro ao carregar texto do documento');
    }
  };

  // ── Copiar ─────────────────────────────────────────────────────────────────
  const copiarTexto = async (doc) => {
    try {
      const res = await axios.get(`/api/documentos/${doc.id}/texto`);
      if (res.data.success) {
        await navigator.clipboard.writeText(res.data.data.texto_integral || '');
        setUploadMsg('📋 Texto copiado para a área de transferência!');
        setTimeout(() => setUploadMsg(''), 2500);
      }
    } catch (e) {
      setErro('Erro ao copiar texto');
    }
  };

  // ── Abrir chat IA ──────────────────────────────────────────────────────────
  const abrirChatIA = async (doc) => {
    try {
      const res = await axios.get(`/api/documentos/${doc.id}/texto`);
      if (res.data.success) {
        const textoIntegral = res.data.data.texto_integral || '';
        if (!textoIntegral.trim()) {
          setErro('Este documento não possui conteúdo extraído ainda');
          return;
        }
        const contextoCompleto = `
===== DOCUMENTO: ${doc.file_name} =====
Tipo: ${tipoLabel(doc)}
Data: ${formatarData(doc.created_at)}
${doc.cod_cli  ? `Cliente: ${doc.cod_cli}`  : ''}
${doc.caso_nome ? `Caso: ${doc.caso_nome}` : ''}

===== CONTEÚDO =====
${textoIntegral}
        `.trim();

        navigate('/chat-with-ai', {
          state: {
            content: contextoCompleto,
            fromDocumento: true,
            documentoId:   doc.id,
            documentoNome: doc.file_name,
            documentoTipo: tipoLabel(doc),
          },
        });
      } else {
        setErro('Erro ao carregar conteúdo do documento');
      }
    } catch (e) {
      setErro('Erro ao abrir chat IA: ' + (e.response?.data?.message || e.message));
    }
  };

  // ── Eliminar ───────────────────────────────────────────────────────────────
  const eliminarDocumento = async (doc) => {
    if (!window.confirm(`Eliminar "${doc.file_name}"?\nEsta acção é irreversível.`)) return;
    try {
      await axios.delete(`/api/documentos/${doc.id}`);
      setDocumentos(prev => prev.filter(d => d.id !== doc.id));
      setUploadMsg('✅ Documento eliminado com sucesso');
      setTimeout(() => setUploadMsg(''), 2500);
    } catch (e) {
      setErro('Erro ao eliminar documento');
    }
  };

  // ── Título do painel conforme tipo detectado ────────────────────────────────
  const tituloPainel = tipoFicheiro === 'audio'
    ? '🎙️ Transcrição de Áudio — Speech-to-Text (pt-PT)'
    : tipoFicheiro === 'ocr'
    ? '🔍 Transcrição OCR — Documento Digitalizado'
    : '📎 Transcrever Documento ou Áudio';

  const hintPainel = tipoFicheiro === 'audio'
    ? 'Formatos áudio: MP3, WAV, FLAC, OGG, WebM · Identifica interlocutores · Máx. 500MB'
    : tipoFicheiro === 'ocr'
    ? 'Formatos imagem/PDF: PDF, JPG, PNG, TIFF, GIF, BMP, WEBP · Máx. 500MB'
    : 'Seleciona um ficheiro — PDF/imagem para OCR, áudio para transcrição de voz';

  const btnLabel = uploadStatus === 'uploading'
    ? `A enviar... ${uploadProgress}%`
    : uploadStatus === 'processing'
    ? '⏳ A processar...'
    : tipoFicheiro === 'audio'
    ? '🎙️ Iniciar Transcrição de Áudio'
    : '🔍 Iniciar Transcrição OCR';

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="md-container">

      {/* ── Header ── */}
      <div className="md-header">
        <div className="md-header-left">
          <h2 className="md-title">Meus Documentos</h2>
          <p className="md-subtitle">Arquivo pessoal · {documentos.length} documento{documentos.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          className="md-btn-transcrever"
          onClick={() => { setMostrarUpload(v => !v); setErro(''); }}
        >
          <IconUpload />
          {mostrarUpload ? 'Fechar' : 'Transcrever Novo Documento'}
        </button>
      </div>

      {/* ── Painel Upload (OCR + Áudio unificado) ── */}
      {mostrarUpload && (
        <div className="md-upload-panel">
          <h3 className="md-upload-title">{tituloPainel}</h3>
          <p className="md-upload-hint">{hintPainel}</p>

          <div className="md-upload-row">
            <div className="md-upload-field">
              <label className="md-label">Código Cliente <small>(opcional)</small></label>
              <input
                className="md-input md-input-small"
                type="text"
                value={uploadCodCli}
                onChange={e => setUploadCodCli(formatarCodCliente(e.target.value))}
                placeholder="00000"
                inputMode="numeric"
                maxLength={5}
              />
            </div>

            <div className="md-upload-field md-upload-field-grow">
              <label className="md-label">Ficheiro (PDF, imagem ou áudio)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept={EXT_TODOS.join(',')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="upload-file-input"
              />
              <label htmlFor="upload-file-input" className="md-file-label">
                {uploadFile
                  ? `${tipoFicheiro === 'audio' ? '🎙️' : '📄'} ${uploadFile.name} (${(uploadFile.size / 1024 / 1024).toFixed(1)} MB)`
                  : '📁 Selecionar ficheiro...'}
              </label>
            </div>
          </div>

          {/* Indicador visual do tipo detectado */}
          {tipoFicheiro && (
            <div className={`md-tipo-badge ${tipoFicheiro === 'audio' ? 'md-tipo-audio' : 'md-tipo-ocr'}`}>
              {tipoFicheiro === 'audio'
                ? '🎙️ Ficheiro de áudio detectado — será transcrito via Google Speech-to-Text (pt-PT)'
                : '📄 Documento/imagem detectado — será transcrito via Google Document AI (OCR)'}
            </div>
          )}

          {uploadStatus === 'uploading' && (
            <div className="md-progress-bar">
              <div className="md-progress-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}

          {uploadMsg && (
            <div className={`md-upload-msg md-upload-msg-${uploadStatus}`}>
              {uploadMsg}
            </div>
          )}

          <div className="md-upload-actions">
            <button
              className="md-btn-primary"
              onClick={iniciarTranscricao}
              disabled={!uploadFile || uploadStatus === 'uploading' || uploadStatus === 'processing'}
            >
              {tipoFicheiro === 'audio' ? <IconMic /> : <IconUpload />}
              {btnLabel}
            </button>
            {uploadFile && uploadStatus === 'idle' && (
              <button className="md-btn-ghost" onClick={() => {
                setUploadFile(null);
                setTipoFicheiro(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}>
                Limpar
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Filtros ── */}
      <div className="md-filtros">
        <div className="md-filtros-header">
          <IconSearch />
          <span>Pesquisar</span>
        </div>
        <div className="md-filtros-row">
          <div className="md-filtro-group">
            <label className="md-label">Tipo de documento</label>
            <select
              className="md-select"
              value={filtroTipo}
              onChange={e => setFiltroTipo(e.target.value)}
            >
              {TIPOS_FILTRO.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="md-filtro-group">
            <label className="md-label">Código Cliente</label>
            <input
              className="md-input md-input-small"
              type="text"
              value={filtroCodCli}
              onChange={e => setFiltroCodCli(formatarCodCliente(e.target.value))}
              placeholder="00000"
              inputMode="numeric"
              maxLength={5}
            />
          </div>
          <div className="md-filtro-group md-filtro-actions">
            <button className="md-btn-refresh" onClick={carregarDocumentos} title="Actualizar">
              <IconRefresh /> Actualizar
            </button>
          </div>
        </div>
      </div>

      {/* ── Mensagem de feedback rápido ── */}
      {uploadMsg && !mostrarUpload && (
        <div className="md-feedback">{uploadMsg}</div>
      )}

      {/* ── Erro ── */}
      {erro && <div className="md-erro">{erro}</div>}

      {/* ── Tabela ── */}
      <div className="md-table-wrap">
        {loading ? (
          <div className="md-empty">
            <span className="md-spinner" />
            <span>A carregar documentos...</span>
          </div>
        ) : documentos.length === 0 ? (
          <div className="md-empty">
            <span style={{ fontSize: '2.5rem' }}>📂</span>
            <span>Nenhum documento encontrado</span>
          </div>
        ) : (
          <table className="md-table">
            <thead>
              <tr>
                <th>Data / Hora</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Ficheiro</th>
                <th>Conteúdo</th>
                <th className="md-th-acoes">Acções</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map(doc => {
                const st     = STATUS_LABEL[doc.ocr_status] || null;
                const isProc = doc.origem === 'document_ai_ocr' || doc.origem === 'speech_to_text' || doc.origem === 'documentos-gerado';
                return (
                  <tr key={doc.id} className="md-tr">
                    <td className="md-td-data">{formatarData(doc.created_at)}</td>
                    <td>
                      <span className={`md-badge ${tipoCls(doc)}`}>{tipoLabel(doc)}</span>
                    </td>
                    <td>
                      {isProc && st
                        ? <span className={`md-status ${st.cls}`}>{st.label}</span>
                        : <span className="md-status-na">N/A</span>}
                    </td>
                    <td className="md-td-file" title={doc.file_name}>{doc.file_name}</td>
                    <td className="md-td-preview">
                      {doc.texto_preview
                        ? <span className="md-preview-text">{doc.texto_preview}…</span>
                        : <span className="md-preview-vazio">—</span>}
                    </td>
                    <td className="md-td-acoes">
                      <button
                        className="md-icon-btn md-icon-ver"
                        title="Ver texto"
                        onClick={() => verTexto(doc)}
                        disabled={!doc.texto_preview}
                      >
                        <IconEye />
                      </button>
                      <button
                        className="md-icon-btn md-icon-copy"
                        title="Copiar texto"
                        onClick={() => copiarTexto(doc)}
                        disabled={!doc.texto_preview}
                      >
                        <IconCopy />
                      </button>
                      <button
                        className="md-icon-btn md-icon-ai"
                        title="Abrir no Chat IA"
                        onClick={() => abrirChatIA(doc)}
                        disabled={!doc.texto_preview}
                      >
                        <IconAI />
                      </button>
                      <button
                        className="md-icon-btn md-icon-del"
                        title="Eliminar"
                        onClick={() => eliminarDocumento(doc)}
                      >
                        <IconTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Dialog viewer ── */}
      {dialog.open && (
        <div className="md-overlay" onClick={() => setDialog({ open: false, titulo: '', texto: '' })}>
          <div className="md-dialog" onClick={e => e.stopPropagation()}>
            <div className="md-dialog-header">
              <span className="md-dialog-titulo">📄 {dialog.titulo}</span>
              <button className="md-dialog-close" onClick={() => setDialog({ open: false, titulo: '', texto: '' })}>
                <IconX />
              </button>
            </div>
            <div className="md-dialog-body">
              <textarea className="md-dialog-textarea" value={dialog.texto} readOnly />
            </div>
            <div className="md-dialog-footer">
              <button
                className="md-btn-primary"
                onClick={() => navigator.clipboard.writeText(dialog.texto).then(() => setUploadMsg('📋 Copiado!'))}
              >
                <IconCopy /> Copiar para transferência
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeusDocumentos;